import { ChangeEventHandler, FC, useMemo } from 'react';

import useSWR from 'swr';

import { CreateRaffleButton } from './createRaffleButton/CreateRaffleButton';
import { RaffleActionsColumn } from './raffleTableColumns/RaffleActionsColumn';

import css from './PiggyBank.module.css';

import { referralRaffleService } from 'v2/api/referralRaffleService';
import { DataTable } from 'v2/components/dataTable/DataTable';
import { Loader } from 'v2/components/loader/Loader';
import { SearchTextField } from 'v2/components/searchTextField/SearchTextField';
import { useFilter } from 'v2/hooks/useFilter';
import { usePagination } from 'v2/hooks/usePagination';
import { TableColumn } from 'v2/types';
import { ReferralRaffle } from 'v2/types/ReferralRaffleModel';
import { formatNumber } from 'v2/utils';

interface RaffleFilter {
  name: string;
}

export const PiggyBank: FC = () => {
  const { filter, debouncedFilter, onChange } = useFilter<RaffleFilter>({ name: '' });
  const pagination = usePagination();

  const { data, isLoading, mutate } = useSWR(
    ['getRaffles', pagination.page, pagination.pageSize, debouncedFilter],
    referralRaffleService.getRaffles,
    { keepPreviousData: true }
  );

  const handleNameChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    onChange('name', e.target.value);
  };

  const handleClearName = () => {
    onChange('name', '');
  };

  const columns: TableColumn<ReferralRaffle>[] = useMemo(
    () => [
      { id: 'name', key: 'name', label: 'Name', ellipsis: true, style: { width: '30%' } },
      {
        key: 'participantsMaxCount',
        label: 'Max Participants',
        render: (row) => formatNumber(row.participantsMaxCount),
      },
      {
        key: 'participantsCount',
        label: 'Participants Count',
        render: (row) => formatNumber(row.participantsCount),
      },
      {
        key: 'rewardValue',
        label: 'Reward Value',
        render: (row) => row.reward.value || '—',
      },
      {
        key: 'actions',
        align: 'right',
        render: (row) => <RaffleActionsColumn raffle={row} onRevalidate={mutate} />,
      },
    ],
    [mutate]
  );

  return (
    <div className={css.piggyBank}>
      <div className={css.piggyBankHeader}>
        <div className={css.piggyBankFilters}>
          <SearchTextField
            value={filter.name}
            onChange={handleNameChange}
            onClear={filter.name ? handleClearName : undefined}
            label="Search by name"
          />
        </div>

        <CreateRaffleButton onSuccess={mutate} />
      </div>

      {isLoading ? (
        <Loader />
      ) : (
        <DataTable
          columns={columns}
          data={data?.result}
          withPagination
          total={data?.total}
          page={pagination.page}
          pageSize={pagination.pageSize}
          onPageChange={pagination.onPageChange}
        />
      )}
    </div>
  );
};
